/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Grid, LinearProgress } from '@mui/material';
import { groupBy } from 'lodash';
import React, { useCallback } from 'react';
import type { RowComponentProps } from 'react-window';
import { List, useListRef } from 'react-window';
import { AlertBanner, LastUpdateTime } from '@opengeoweb/shared';
import { breakpoints } from '@opengeoweb/theme';
import { NewWarningButton } from './NewWarningButton/NewWarningButton';
import WarningListHeaderContent from './WarningListHeaderContent';
import WarningListItem from './WarningListItem';
import type { WarningListItemActions } from './WarningListItemMenu';
import WarningListItemSeperator from './WarningListItemSeperator';
import type {
  MaritimeWarning,
  MaritimeWarningAction,
  Warning,
} from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import { mapProductStatusToWarningStatus } from '../../utils/sig-airmet-util';
import { useGetFilterHeight } from '../AreaObjectLoader/AreasObjects';
import type { FilterHookState } from '../FilterList/filter-hooks';
import FilterList from '../FilterList/FilterList';

const RowComponent = ({
  index,
  style,
  items,
  expandedSections,
  hoveredFeatureId,
  selectedWarningId,
  onSelectWarning,
  onEditWarning,
  onDeleteWarning,
  onDeleteReviewWarning,
  onExpireWarning,
  onWithdrawWarning,
  toggleExpand,
  timeFilter,
  onActionAviationWarning,
  onActionMaritimeWarning,
}: RowComponentProps<{
  items: (Warning | WarningListElementHeader)[];
  expandedSections: Record<string, boolean>;
  hoveredFeatureId?: string;
  selectedWarningId: string;
  onSelectWarning: (warning: Warning) => void;
  onEditWarning: (warning: Warning) => void;
  onDeleteWarning: (warningId: string, warningEditor?: string) => void;
  onDeleteReviewWarning: (warning: Warning) => void;
  onExpireWarning: (warning: Warning) => void;
  onWithdrawWarning: (warning: Warning) => void;
  onActionAviationWarning: (warning: Warning) => void;
  onActionMaritimeWarning: (
    warning: MaritimeWarning,
    actionType?: MaritimeWarningAction,
  ) => void;
  toggleExpand: (shouldToggle: boolean, status: PublicWarningStatus) => void;
  timeFilter: FilterHookState['timeFilter'];
}>): React.ReactElement => {
  const warning = items[index];
  const key = `warning-${warning.status}-${index}`;
  if ((warning as WarningListElementHeader).totalWarnings !== undefined) {
    const header = warning as WarningListElementHeader;
    return (
      <WarningListItemSeperator
        isExpanded={expandedSections[warning.status!]}
        key={key}
        onClick={() => {
          toggleExpand(!expandedSections[header.status], header.status);
        }}
        status={header.status}
        style={style}
        timeFilter={timeFilter}
        totalWarnings={header.totalWarnings}
      />
    );
  }

  const publicWarning = warning as Warning;
  const highlightedItem = hoveredFeatureId === publicWarning.id;

  return (
    <WarningListItem
      activeWarningId={selectedWarningId}
      highlightedItem={highlightedItem}
      key={key}
      onActionAviationWarning={onActionAviationWarning}
      onActionMaritimeWarning={onActionMaritimeWarning}
      onDeleteReviewWarning={onDeleteReviewWarning}
      onDeleteWarning={onDeleteWarning}
      onEditWarning={onEditWarning}
      onExpireWarning={onExpireWarning}
      onSelectWarning={onSelectWarning}
      onWithdrawWarning={onWithdrawWarning}
      style={style}
      warning={publicWarning}
    />
  );
};

interface SortedWarnings {
  draft: Warning[];
  published: Warning[];
  todo: Warning[];
  expired: Warning[];
}

type SortKeyType = 'lastUpdatedTime' | 'validFrom' | 'validUntil';
interface SortState {
  key: SortKeyType;
  direction: 'asc' | 'desc';
}

const groupWarningsOnStatus = (
  warnings: Warning[],
  sortState: SortState,
): SortedWarnings => {
  const { key, direction } = sortState;
  const sortedWarnings = warnings.sort((warningA, warningB) => {
    const dateStringA =
      key === 'lastUpdatedTime' ? warningA[key] : warningA.warningDetail[key];
    const dateStringB =
      key === 'lastUpdatedTime' ? warningB[key] : warningB.warningDetail[key];

    return dateStringB?.localeCompare(dateStringA ?? '') ?? 0;
  });
  if (direction === 'asc') {
    sortedWarnings.reverse();
  }
  const groupedWarnings = groupBy(sortedWarnings, (warning) => {
    const status =
      warning.status ?? mapProductStatusToWarningStatus(warning.productStatus);
    switch (status) {
      case PublicWarningStatus.DRAFT:
      case PublicWarningStatus.DRAFT_PUBLISHED:
        return 'draft';
      case PublicWarningStatus.PUBLISHED:
        return 'published';
      case PublicWarningStatus.TODO:
        return 'todo';
      case PublicWarningStatus.EXPIRED:
      case PublicWarningStatus.WITHDRAWN:
        return 'expired';
      default:
        return 'ignore';
    }
  });
  return {
    draft: [],
    published: [],
    todo: [],
    expired: [],
    ...groupedWarnings,
  };
};

export interface PublicWarningListProps extends WarningListItemActions {
  warnings: Warning[];
  onSelectWarning?: (warning: Warning) => void;
  isLoading?: boolean;
  error?: Error;
  selectedWarningId?: string;
  lastUpdatedTime?: string;
  onCreateWarning?: () => void;
  onRefetchWarnings?: () => void;
  filterHookState: FilterHookState;
  hoveredFeatureId?: string;
  expandedSections: Record<string, boolean>;
  setExpandedSections: (expandedSections: Record<string, boolean>) => void;
  onActionAviationWarning?: () => void;
  limitAlertCount?: number;
  showLimitAlert?: boolean;
}

interface WarningListElementHeader {
  status: PublicWarningStatus;
  totalWarnings: number;
}

export const PublicWarningList: React.FC<PublicWarningListProps> = ({
  warnings = [],
  filterHookState,
  isLoading = false,
  error,
  selectedWarningId = '',
  lastUpdatedTime,
  onSelectWarning = (): void => {},
  onEditWarning = (): void => {},
  onCreateWarning = (): void => {},
  onDeleteWarning = (): void => {},
  onRefetchWarnings = (): void => {},
  onDeleteReviewWarning = (): void => {},
  onExpireWarning = (): void => {},
  onWithdrawWarning = (): void => {},
  onActionAviationWarning = (): void => {},
  onActionMaritimeWarning = (): void => {},
  hoveredFeatureId,
  expandedSections,
  setExpandedSections,
  limitAlertCount,
  showLimitAlert,
}) => {
  const {
    updateFilter,
    allSelected,
    filterState,
    timeFilter,
    updateTimeFilter,
  } = filterHookState;
  const { t } = useWarningsTranslation();
  const [sortState, setSortState] = React.useState<SortState>({
    key: 'lastUpdatedTime',
    direction: 'desc',
  });

  const onClickAll = (isChecked: boolean): void => {
    updateFilter('ALL', 'ALL', !isChecked);
  };
  const sortedWarnings = React.useMemo(
    () => groupWarningsOnStatus(warnings, sortState),
    [warnings, sortState],
  );

  const toggleSortDirection = (key: SortKeyType): void => {
    setSortState((prevState) => ({
      key,
      direction:
        prevState.key === key && prevState.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const toggleExpand = (
    shouldToggle: boolean,
    status: PublicWarningStatus,
  ): void => {
    setExpandedSections({
      ...expandedSections,
      [status]: shouldToggle,
    });
    resetListIndex();
  };

  const items: (Warning | WarningListElementHeader)[] = React.useMemo(
    () => [
      {
        status: PublicWarningStatus.TODO,
        totalWarnings: sortedWarnings.todo.length,
      },
      ...(expandedSections.TODO ? sortedWarnings.todo : []),
      {
        status: PublicWarningStatus.DRAFT,
        totalWarnings: sortedWarnings.draft.length,
      },
      ...(expandedSections.DRAFT ? sortedWarnings.draft : []),
      {
        status: PublicWarningStatus.PUBLISHED,
        totalWarnings: sortedWarnings.published.length,
      },
      ...(expandedSections.PUBLISHED ? sortedWarnings.published : []),
      {
        status: PublicWarningStatus.EXPIRED,
        totalWarnings: sortedWarnings.expired.length,
      },
      ...(expandedSections.EXPIRED ? sortedWarnings.expired : []),
    ],
    [sortedWarnings, expandedSections],
  );

  const getItemSize = (index: number, width: number): number => {
    const item = items[index];
    if ((item as WarningListElementHeader).totalWarnings !== undefined) {
      return 36;
    }
    if (width < 400) {
      return 201;
    }
    if (width < 500) {
      return 101;
    }
    if (width < breakpoints.tablet) {
      return 81;
    }
    if (width < breakpoints.desktop) {
      return 71;
    }

    return 34;
  };

  const filterRef = React.useRef<HTMLDivElement>(null);
  const listRef = useListRef(null);

  const resetListIndex = useCallback((): void => {
    if (listRef.current) {
      listRef.current?.scrollToRow({
        index: 0,
        behavior: 'instant',
      });
    }
  }, [listRef]);

  React.useEffect(() => {
    if (listRef.current) {
      resetListIndex();
    }
  }, [listRef, resetListIndex, warnings]);

  const filterHeight = useGetFilterHeight(
    filterRef as React.MutableRefObject<HTMLElement>,
  );

  React.useEffect(() => {
    if (hoveredFeatureId) {
      const index = items.findIndex(
        (item) => (item as Warning).id === hoveredFeatureId,
      );

      listRef.current?.scrollToRow({
        index,
        behavior: 'smooth',
      });
    }
  }, [hoveredFeatureId, items, listRef]);

  return (
    <>
      {isLoading ? (
        <LinearProgress
          color="secondary"
          data-testid="loading-bar"
          sx={{ position: 'absolute', width: '100%', top: 0 }}
        />
      ) : null}
      <Box
        data-testid="publicWarningList"
        sx={{
          padding: 3,
          height: '100%',
          overflowY: 'hidden',
          containerName: 'publicWarningList',
          containerType: 'inline-size',
        }}
      >
        <Box
          ref={filterRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {lastUpdatedTime ? (
              <LastUpdateTime
                lastUpdateTime={lastUpdatedTime}
                onPressRefresh={onRefetchWarnings}
              />
            ) : (
              <Box />
            )}
            <NewWarningButton onCreateWarning={onCreateWarning} />
          </Box>

          <Box>
            <FilterList
              filterState={filterState}
              isAllSelected={allSelected}
              onClickAll={onClickAll}
              onUpdateTimeFilter={updateTimeFilter}
              timeFilter={timeFilter}
              updateFilter={updateFilter}
            />
          </Box>
        </Box>

        {error ? <AlertBanner shouldClose title={error.message} /> : null}
        {!!showLimitAlert && (
          <Box sx={{ mb: 2 }}>
            <AlertBanner
              dataTestId="warning-list-over-limit"
              info={t('warning-list-too-many-info', {
                count: limitAlertCount,
              })}
              severity="warning"
              title={t('warning-list-too-many-title')}
            />
          </Box>
        )}
        {!error && (
          <Grid
            alignItems="center"
            columnSpacing={0}
            container
            justifyContent="space-between"
            rowSpacing={0}
            size="grow"
            sx={{
              maxWidth: '100%',
              display: 'none',
              marginTop: '4px',
              [`@container publicWarningList (min-width: ${breakpoints.desktop}px)`]:
                {
                  display: 'flex',
                },
            }}
          >
            <WarningListHeaderContent
              sx={{ paddingLeft: 2 }}
              title={t('warning-list-header-incidents')}
            />
            <WarningListHeaderContent
              isSorted={sortState.key === 'lastUpdatedTime'}
              onClick={() => toggleSortDirection('lastUpdatedTime')}
              sortDirection={sortState.direction}
              sortable
              title={`${t('warning-list-header-last-update')} UTC`}
              width={142}
            />
            <WarningListHeaderContent
              isSorted={sortState.key === 'validFrom'}
              onClick={() => toggleSortDirection('validFrom')}
              sortDirection={sortState.direction}
              sortable
              title={`${t('warning-list-header-start-time')} UTC`}
              width={142}
            />
            <WarningListHeaderContent
              isSorted={sortState.key === 'validUntil'}
              onClick={() => toggleSortDirection('validUntil')}
              sortDirection={sortState.direction}
              sortable
              title={`${t('warning-list-header-end-time')} UTC`}
              width={142}
            />

            <WarningListHeaderContent
              title={t('warning-list-header-domains')}
            />
            <WarningListHeaderContent
              title={t('warning-list-header-phenomena')}
              width={140}
            />
            <WarningListHeaderContent
              sx={{
                [`@container publicWarningList (min-width: ${breakpoints.desktop}px)`]:
                  {
                    width: 56,
                  },
              }}
              title={t('warning-list-header-levels')}
            />
            <WarningListHeaderContent title={t('areas')} width={100} />
            <WarningListHeaderContent
              sx={{ marginRight: '48px' }}
              title={t('warning-list-header-status')}
              width={76}
            />
          </Grid>
        )}
        {sortedWarnings && !error ? (
          <Box
            data-testid="warnings-list"
            sx={{
              height: `calc(100% - ${filterHeight}px)`,
              minHeight: '206px',
            }}
          >
            <List
              listRef={listRef}
              rowComponent={RowComponent}
              rowCount={items.length}
              rowHeight={(index) =>
                getItemSize(index, listRef.current?.element?.clientWidth || 0)
              }
              rowProps={{
                items,
                expandedSections,
                hoveredFeatureId,
                selectedWarningId,
                onSelectWarning,
                onEditWarning,
                onDeleteWarning,
                onDeleteReviewWarning,
                onExpireWarning,
                onWithdrawWarning,
                toggleExpand,
                timeFilter,
                onActionAviationWarning,
                onActionMaritimeWarning,
              }}
            />
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export default PublicWarningList;
